import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Button,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  FaStore,
  FaHistory,
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaFileAlt,
  FaLock,
} from "react-icons/fa";
import {
  getUserStores,
  getStoreHistory,
  getStoreStatusByDate,
  getAllStoreStatuses,
  selectStoreStatus,
  StoreStatusState,
  setPagination,
  setSorting,
  SortDirection,
  SortField,
} from "../slices/store-status.slice";

// Helper functions for date formatting
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`; // yyyy-mm-dd in local timezone
};

const formatDateToUSFormat = (date: Date): string => {
  return date.toLocaleDateString("en-US"); // mm/dd/yyyy display
};

const formatAPIDateToDisplay = (apiDate: string): string => {
  const date = new Date(apiDate + "T00:00:00"); // Ensure proper parsing
  return date.toLocaleDateString("en-US"); // mm/dd/yyyy
};

export function StoreStatusHistory() {
  const dispatch = useAppDispatch();
  const storeStatusState = useAppSelector(selectStoreStatus);

  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<"store" | "date">("store");

  // FIXED: All hooks must be called at the top level
  useEffect(() => {
    dispatch(getUserStores());
  }, [dispatch]);

  // FIXED: Auto-select store and load data properly for admins
  useEffect(() => {
    // Wait for user stores and admin status to be loaded
    if (storeStatusState.userStores.length > 0 && viewMode === "store") {
      console.log("Admin status:", storeStatusState.isAdmin);
      console.log("User stores:", storeStatusState.userStores.length);

      if (storeStatusState.isAdmin) {
        // For admin: Load all statuses immediately, regardless of store selection
        console.log("Loading all statuses for admin");
        handleLoadAllStatuses();

        // If only one store, auto-select it for display purposes
        if (storeStatusState.userStores.length === 1 && !selectedStore) {
          setSelectedStore(storeStatusState.userStores[0]);
        }
      } else if (storeStatusState.userStores.length === 1 && !selectedStore) {
        // For regular user with single store: auto-select and load its history
        const singleStore = storeStatusState.userStores[0];
        setSelectedStore(singleStore);
        console.log(
          "Auto-selected single store for regular user:",
          singleStore.store_name
        );
        dispatch(getStoreHistory(singleStore.store_id));
      }
    }
  }, [
    storeStatusState.userStores,
    storeStatusState.isAdmin,
    viewMode,
    selectedStore,
  ]);

  // FIXED: Load all statuses for admin with proper typing
  const handleLoadAllStatuses = (
    page: number = 1,
    sortField: SortField = "status_date",
    sortDirection: SortDirection = "desc"
  ) => {
    console.log("Dispatching getAllStoreStatuses with params:", {
      page,
      pageSize: storeStatusState.pagination.pageSize,
      sortField,
      sortDirection,
    });
    dispatch(
      getAllStoreStatuses({
        page,
        pageSize: storeStatusState.pagination.pageSize,
        sortField,
        sortDirection,
      })
    );
  };

  // FIXED: Handle permission check after all hooks are called
  if (storeStatusState.permissionError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaLock className="text-red-500" size={24} />
            </div>
            <Typography variant="h5" className="text-gray-800 mb-2">
              Access Denied
            </Typography>
            <Typography variant="body1" className="text-gray-600 mb-4">
              {storeStatusState.permissionError}
            </Typography>
            <Typography variant="body2" color="textSecondary" className="mb-4">
              Only Team Captains, Managers, and Admins can access Store Status
              History.
            </Typography>
            <Button variant="outlined" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading while checking permissions
  if (
    storeStatusState.status === StoreStatusState.inProgress &&
    !storeStatusState.hasPermission &&
    !storeStatusState.permissionError
  ) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <CircularProgress />
        <Typography className="ml-2">Checking permissions...</Typography>
      </div>
    );
  }

  const handleStoreSelect = (store: any) => {
    setSelectedStore(store);
    if (store) {
      if (storeStatusState.isAdmin) {
        // Admin sees all statuses across all stores
        console.log("Admin selected store, loading all statuses");
        handleLoadAllStatuses();
      } else {
        // Regular users see single store history
        console.log("Regular user selected store, loading store history");
        dispatch(getStoreHistory(store.store_id));
      }
    }
  };

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      const apiDate = formatDateForAPI(date);
      console.log("Searching for date (API format):", apiDate);
      console.log("Display date:", formatDateToUSFormat(date));
      dispatch(getStoreStatusByDate({ date: apiDate }));
    }
  };

  // Handle pagination change
  const handlePageChange = (event: unknown, newPage: number) => {
    const page = newPage + 1; // MUI uses 0-based indexing
    dispatch(setPagination({ currentPage: page }));
    handleLoadAllStatuses(
      page,
      storeStatusState.sorting.field,
      storeStatusState.sorting.direction
    );
  };

  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pageSize = parseInt(event.target.value, 10);
    dispatch(setPagination({ pageSize, currentPage: 1 }));
    handleLoadAllStatuses(
      1,
      storeStatusState.sorting.field,
      storeStatusState.sorting.direction
    );
  };

  // Handle sort change with proper typing
  const handleSortChange = (field: SortField) => {
    const isAsc =
      storeStatusState.sorting.field === field &&
      storeStatusState.sorting.direction === "asc";
    const direction: SortDirection = isAsc ? "desc" : "asc";

    dispatch(setSorting({ field, direction }));
    handleLoadAllStatuses(
      storeStatusState.pagination.currentPage,
      field,
      direction
    );
  };

  const isLoading = storeStatusState.status === StoreStatusState.inProgress;
  const hasOnlyOneStore = storeStatusState.userStores.length === 1;

  const getStatusIcon = (status: string) => {
    return status === "open" ? (
      <FaCheckCircle className="text-green-500" size={16} />
    ) : (
      <FaTimesCircle className="text-red-500" size={16} />
    );
  };

  const getStatusChip = (status: string) => {
    return (
      <Chip
        label={status.toUpperCase()}
        color={status === "open" ? "success" : "error"}
        size="small"
        icon={getStatusIcon(status)}
      />
    );
  };

  // FIXED: Choose which data to display with better logic
  const displayData =
    storeStatusState.isAdmin && viewMode === "store"
      ? storeStatusState.allStoreStatuses
      : storeStatusState.storeHistory;

  console.log("Display data:", displayData);
  console.log("Is admin:", storeStatusState.isAdmin);
  console.log("View mode:", viewMode);
  console.log("All store statuses:", storeStatusState.allStoreStatuses);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
            <FaHistory className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Store Status History
            </h1>
            <p className="text-gray-600">
              View historical store open/closed status data
            </p>
          </div>
        </div>
      </div>

      <Grid container spacing={4}>
        {/* Filters */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4 font-semibold">
                Search Filters
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <div className="space-y-4">
                    <div className="flex space-x-2 mb-4">
                      <Button
                        variant={
                          viewMode === "store" ? "contained" : "outlined"
                        }
                        onClick={() => {
                          setViewMode("store");
                          setSelectedDate(null);
                          // FIXED: Reload data when switching to store mode
                          if (storeStatusState.isAdmin) {
                            handleLoadAllStatuses();
                          }
                        }}
                        startIcon={<FaStore />}
                      >
                        By Store
                      </Button>
                      <Button
                        variant={viewMode === "date" ? "contained" : "outlined"}
                        onClick={() => {
                          setViewMode("date");
                          setSelectedStore(null);
                        }}
                        startIcon={<FaSearch />}
                      >
                        By Date
                      </Button>
                    </div>

                    {viewMode === "store" && (
                      <Autocomplete
                        options={storeStatusState.userStores}
                        getOptionLabel={(option) =>
                          `${option.store_name} - ${option.address}`
                        }
                        value={selectedStore}
                        onChange={(_, newValue) => handleStoreSelect(newValue)}
                        disabled={hasOnlyOneStore || isLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Store"
                            placeholder={
                              hasOnlyOneStore
                                ? "Only one store available"
                                : "Choose a store to view its history"
                            }
                            helperText={
                              hasOnlyOneStore
                                ? "Auto-selected: Only one store assigned"
                                : storeStatusState.isAdmin
                                ? "Admin: Viewing all store statuses"
                                : "Choose a store to view its history"
                            }
                          />
                        )}
                      />
                    )}

                    {viewMode === "date" && (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Select Date"
                          value={selectedDate}
                          onChange={handleDateSelect}
                          maxDate={new Date()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              placeholder="Choose a date (mm/dd/yyyy)"
                              helperText={
                                selectedDate
                                  ? `Selected: ${formatDateToUSFormat(
                                      selectedDate
                                    )}`
                                  : "Choose a date to view all store statuses"
                              }
                            />
                          )}
                          disabled={isLoading}
                        />
                      </LocalizationProvider>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} md={6}>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <Typography
                      variant="body2"
                      className="font-medium text-gray-800 mb-2"
                    >
                      Quick Stats
                    </Typography>
                    <div className="space-y-2">
                      <Typography
                        variant="caption"
                        className="text-gray-600 block"
                      >
                        Total Stores: {storeStatusState.userStores.length}
                        {hasOnlyOneStore && " (Auto-selected)"}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="text-gray-600 block"
                      >
                        Access Level:{" "}
                        {storeStatusState.isAdmin
                          ? "Admin (All Store Statuses)"
                          : "Store Manager"}
                      </Typography>
                      {viewMode === "store" && (
                        <Typography
                          variant="caption"
                          className="text-blue-600 block"
                        >
                          {storeStatusState.isAdmin
                            ? `Viewing: All store statuses (${displayData.length} records)`
                            : selectedStore
                            ? `Viewing: ${selectedStore.store_name}`
                            : "No store selected"}
                        </Typography>
                      )}
                      {viewMode === "date" && selectedDate && (
                        <Typography
                          variant="caption"
                          className="text-blue-600 block"
                        >
                          Date: {formatDateToUSFormat(selectedDate)}
                        </Typography>
                      )}
                    </div>
                  </div>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Results */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <Typography variant="h6" className="font-semibold">
                  {viewMode === "store"
                    ? storeStatusState.isAdmin
                      ? "All Store Statuses"
                      : "Store History"
                    : "Daily Status Report"}
                </Typography>
                {isLoading && <CircularProgress size={24} />}
              </div>

              {storeStatusState.status === StoreStatusState.fail && (
                <Alert severity="error" className="mb-4">
                  {storeStatusState.message}
                </Alert>
              )}

              {/* Store History View */}
              {viewMode === "store" && (
                <>
                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell
                            className="font-semibold"
                            sx={{
                              color: "#1f2937 !important",
                              "& .MuiTableSortLabel-root": {
                                color: "#1f2937 !important",
                              },
                              "& .MuiTableSortLabel-root:hover": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-root.Mui-active": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: "#1f2937 !important",
                              },
                            }}
                          >
                            <TableSortLabel
                              active={
                                storeStatusState.sorting.field === "status_date"
                              }
                              direction={
                                storeStatusState.sorting.field === "status_date"
                                  ? storeStatusState.sorting.direction
                                  : "asc"
                              }
                              onClick={() => handleSortChange("status_date")}
                              sx={{
                                color: "#1f2937 !important",
                                "&:hover": {
                                  color: "#111827 !important",
                                },
                                "&.Mui-active": {
                                  color: "#111827 !important",
                                },
                                "& .MuiTableSortLabel-icon": {
                                  color: "#1f2937 !important",
                                },
                              }}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            className="font-semibold"
                            sx={{
                              color: "#1f2937 !important",
                              "& .MuiTableSortLabel-root": {
                                color: "#1f2937 !important",
                              },
                              "& .MuiTableSortLabel-root:hover": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-root.Mui-active": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: "#1f2937 !important",
                              },
                            }}
                          >
                            <TableSortLabel
                              active={
                                storeStatusState.sorting.field === "store_name"
                              }
                              direction={
                                storeStatusState.sorting.field === "store_name"
                                  ? storeStatusState.sorting.direction
                                  : "asc"
                              }
                              onClick={() => handleSortChange("store_name")}
                              sx={{
                                color: "#1f2937 !important",
                                "&:hover": {
                                  color: "#111827 !important",
                                },
                                "&.Mui-active": {
                                  color: "#111827 !important",
                                },
                                "& .MuiTableSortLabel-icon": {
                                  color: "#1f2937 !important",
                                },
                              }}
                            >
                              Store Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            className="font-semibold"
                            sx={{
                              color: "#1f2937 !important",
                              "& .MuiTableSortLabel-root": {
                                color: "#1f2937 !important",
                              },
                              "& .MuiTableSortLabel-root:hover": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-root.Mui-active": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: "#1f2937 !important",
                              },
                            }}
                          >
                            <TableSortLabel
                              active={
                                storeStatusState.sorting.field === "status"
                              }
                              direction={
                                storeStatusState.sorting.field === "status"
                                  ? storeStatusState.sorting.direction
                                  : "asc"
                              }
                              onClick={() => handleSortChange("status")}
                              sx={{
                                color: "#1f2937 !important",
                                "&:hover": {
                                  color: "#111827 !important",
                                },
                                "&.Mui-active": {
                                  color: "#111827 !important",
                                },
                                "& .MuiTableSortLabel-icon": {
                                  color: "#1f2937 !important",
                                },
                              }}
                            >
                              Status
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            className="font-semibold"
                            sx={{ color: "#1f2937 !important" }}
                          >
                            Notes
                          </TableCell>
                          <TableCell
                            className="font-semibold"
                            sx={{
                              color: "#1f2937 !important",
                              "& .MuiTableSortLabel-root": {
                                color: "#1f2937 !important",
                              },
                              "& .MuiTableSortLabel-root:hover": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-root.Mui-active": {
                                color: "#111827 !important",
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: "#1f2937 !important",
                              },
                            }}
                          >
                            <TableSortLabel
                              active={
                                storeStatusState.sorting.field === "updated_at"
                              }
                              direction={
                                storeStatusState.sorting.field === "updated_at"
                                  ? storeStatusState.sorting.direction
                                  : "asc"
                              }
                              onClick={() => handleSortChange("updated_at")}
                              sx={{
                                color: "#1f2937 !important",
                                "&:hover": {
                                  color: "#111827 !important",
                                },
                                "&.Mui-active": {
                                  color: "#111827 !important",
                                },
                                "& .MuiTableSortLabel-icon": {
                                  color: "#1f2937 !important",
                                },
                              }}
                            >
                              Updated
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayData.length > 0 ? (
                          displayData.map((record) => (
                            <TableRow key={record.id} hover>
                              <TableCell sx={{ color: "#1f2937" }}>
                                <Typography
                                  variant="body2"
                                  className="font-medium"
                                  sx={{ color: "#1f2937" }}
                                >
                                  {formatAPIDateToDisplay(record.status_date)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ color: "#1f2937" }}>
                                <Typography
                                  variant="body2"
                                  className="font-medium text-blue-600"
                                  sx={{ color: "#2563eb" }}
                                >
                                  {record.store_name ||
                                    selectedStore?.store_name ||
                                    "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ color: "#1f2937" }}>
                                {getStatusChip(record.status)}
                              </TableCell>
                              <TableCell sx={{ color: "#1f2937" }}>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#6b7280" }}
                                >
                                  {record.notes || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ color: "#1f2937" }}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af" }}
                                >
                                  {new Date(
                                    record.created_at || record.updated_at
                                  ).toLocaleString("en-US")}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : selectedStore ||
                          hasOnlyOneStore ||
                          storeStatusState.isAdmin ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="text-center py-8"
                              sx={{ color: "#6b7280" }}
                            >
                              <FaFileAlt
                                size={32}
                                className="mx-auto mb-2 text-gray-400"
                              />
                              <Typography variant="body2" color="textSecondary">
                                {storeStatusState.isAdmin
                                  ? "No status records found"
                                  : `No history found for ${
                                      selectedStore?.store_name ||
                                      storeStatusState.userStores[0]?.store_name
                                    }`}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="text-center py-8"
                              sx={{ color: "#6b7280" }}
                            >
                              <FaStore
                                size={32}
                                className="mx-auto mb-2 text-gray-400"
                              />
                              <Typography variant="body2" color="textSecondary">
                                Select a store to view its status history
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination for admin */}
                  {storeStatusState.isAdmin &&
                    viewMode === "store" &&
                    displayData.length > 0 && (
                      <TablePagination
                        component="div"
                        count={storeStatusState.pagination.totalRecords}
                        page={storeStatusState.pagination.currentPage - 1}
                        onPageChange={handlePageChange}
                        rowsPerPage={storeStatusState.pagination.pageSize}
                        onRowsPerPageChange={handlePageSizeChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        showFirstButton
                        showLastButton
                        sx={{
                          "& .MuiTablePagination-toolbar": {
                            color: "#1f2937",
                          },
                          "& .MuiTablePagination-selectLabel": {
                            color: "#1f2937",
                          },
                          "& .MuiTablePagination-displayedRows": {
                            color: "#1f2937",
                          },
                        }}
                      />
                    )}
                </>
              )}

              {/* Date View */}
              {viewMode === "date" && (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell
                          className="font-semibold"
                          sx={{ color: "#1f2937 !important" }}
                        >
                          Store Name
                        </TableCell>
                        <TableCell
                          className="font-semibold"
                          sx={{ color: "#1f2937 !important" }}
                        >
                          Address
                        </TableCell>
                        <TableCell
                          className="font-semibold"
                          sx={{ color: "#1f2937 !important" }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          className="font-semibold"
                          sx={{ color: "#1f2937 !important" }}
                        >
                          Notes
                        </TableCell>
                        <TableCell
                          className="font-semibold"
                          sx={{ color: "#1f2937 !important" }}
                        >
                          Updated
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {storeStatusState.storeStatuses.length > 0 ? (
                        storeStatusState.storeStatuses.map((status) => (
                          <TableRow key={status.id} hover>
                            <TableCell sx={{ color: "#1f2937" }}>
                              <Typography
                                variant="body2"
                                className="font-medium"
                                sx={{ color: "#1f2937" }}
                              >
                                {status.store_name}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: "#1f2937" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b7280" }}
                              >
                                {status.address}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: "#1f2937" }}>
                              {getStatusChip(status.status)}
                            </TableCell>
                            <TableCell sx={{ color: "#1f2937" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b7280" }}
                              >
                                {status.notes || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: "#1f2937" }}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#9ca3af" }}
                              >
                                {new Date(
                                  status.updated_at || status.created_at || ""
                                ).toLocaleString("en-US")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : selectedDate ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center py-8"
                            sx={{ color: "#6b7280" }}
                          >
                            <FaFileAlt
                              size={32}
                              className="mx-auto mb-2 text-gray-400"
                            />
                            <Typography variant="body2" color="textSecondary">
                              No status data found for{" "}
                              {formatDateToUSFormat(selectedDate)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center py-8"
                            sx={{ color: "#6b7280" }}
                          >
                            <FaSearch
                              size={32}
                              className="mx-auto mb-2 text-gray-400"
                            />
                            <Typography variant="body2" color="textSecondary">
                              Select a date to view all store statuses
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}
